/**
 * AUTH CONTRACT
 *
 * Frontend mirror of backend authentication rules.
 * Source of truth: docs/auth-contract.md
 *
 * This file defines stable enums and types for:
 * - Error codes returned by auth endpoints
 * - Lifecycle states for orgs/suborgs
 * - Session state transitions
 */

/**
 * HTTP Status codes used in auth contract
 */
export const HTTP_STATUS = {
  OK: 200,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
} as const

/**
 * Error codes that may appear in backend responses
 * These are semantic identifiers, not necessarily in the response body,
 * but inferred from HTTP status + message pattern
 */
export const AUTH_ERROR_CODE = {
  // Login errors
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ORG_DELETED: 'ORG_DELETED',
  SUBORG_DELETED: 'SUBORG_DELETED',
  ACCESS_REVOKED: 'ACCESS_REVOKED',

  // Token errors
  INVALID_TOKEN: 'INVALID_TOKEN',
  EXPIRED_TOKEN: 'EXPIRED_TOKEN',
  NO_TOKEN: 'NO_TOKEN',

  // Misc
  SESSION_INVALIDATED: 'SESSION_INVALIDATED',
  UNKNOWN: 'UNKNOWN',
} as const

export type AuthErrorCode = (typeof AUTH_ERROR_CODE)[keyof typeof AUTH_ERROR_CODE]

/**
 * Lifecycle states for orgs and suborgs
 */
export const ENTITY_STATE = {
  ACTIVE: 'active',
  SOFT_DELETED: 'soft_deleted',
  RESTORED: 'restored',
} as const

export type EntityState = (typeof ENTITY_STATE)[keyof typeof ENTITY_STATE]

/**
 * User context state after login
 */
export const USER_STATUS = {
  IDLE: 'idle',
  AUTHENTICATING: 'authenticating',
  AUTHENTICATED_TOKEN: 'authenticated_token',
  SESSION_READY: 'session_ready',
  SESSION_INVALIDATED: 'session_invalidated',
  ERROR: 'error',
} as const

export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS]

/**
 * Rules for user operational state
 *
 * Per auth-contract.md section "User State Model":
 * A user is ACTIVE only when ALL are true:
 * - Credentials valid
 * - Organization not deleted
 * - Sub-org not deleted (if applicable)
 */
export const USER_OPERATIONAL_RULES = {
  /**
   * If user's organization is soft-deleted, they cannot:
   * - Login (unless superadmin)
   * - Access any org-scoped endpoints
   * - Use valid token for any org operations
   */
  ORG_DELETED_BLOCKS_ACCESS: true,

  /**
   * If user's sub-org is soft-deleted, they cannot:
   * - Access suborg-scoped endpoints
   * - Use token for suborg operations
   */
  SUBORG_DELETED_BLOCKS_ACCESS: true,

  /**
   * Superadmins bypass org-deleted check on login
   */
  SUPERADMIN_BYPASSES_ORG_DELETED: true,

  /**
   * Any 403 on authenticated endpoint = session invalidation
   */
  AUTHENTICATED_403_INVALIDATES_SESSION: true,
} as const

/**
 * Parse error code from backend response
 *
 * Backend contract doesn't mandate error_code field,
 * so frontend infers from HTTP status + message patterns.
 */
export const parseAuthErrorCode = (
  statusCode: number | null,
  message: string | null,
): AuthErrorCode => {
  if (!statusCode || !message) {
    return AUTH_ERROR_CODE.UNKNOWN
  }

  if (statusCode === HTTP_STATUS.UNAUTHORIZED) {
    return AUTH_ERROR_CODE.INVALID_CREDENTIALS
  }

  if (statusCode === HTTP_STATUS.FORBIDDEN) {
    const msgLower = message.toLowerCase()

    // Check sub-org FIRST (to avoid matching "organization" in "sub-organization")
    if (
      (msgLower.includes('sub-organization') || msgLower.includes('suborg')) &&
      msgLower.includes('deleted')
    ) {
      return AUTH_ERROR_CODE.SUBORG_DELETED
    }

    if (
      msgLower.includes('organization') &&
      (msgLower.includes('deleted') || msgLower.includes('deactivated'))
    ) {
      return AUTH_ERROR_CODE.ORG_DELETED
    }

    // Post-login 403 without org/suborg mention = generic access revoked
    return AUTH_ERROR_CODE.ACCESS_REVOKED
  }

  if (statusCode === HTTP_STATUS.BAD_REQUEST) {
    const msgLower = message.toLowerCase()
    if (msgLower.includes('token')) {
      return AUTH_ERROR_CODE.INVALID_TOKEN
    }
  }

  return AUTH_ERROR_CODE.UNKNOWN
}

/**
 * Determine if an error should trigger session invalidation
 *
 * Rule: any 403 on authenticated endpoint = session invalidation
 *
 * Per auth-contract.md "Frontend implication" section:
 * If a 403 is returned from any authenticated endpoint (outside of login),
 * the frontend should treat this as a session invalidation event.
 */
export const shouldInvalidateSession = (
  statusCode: number | null,
  errorCode: AuthErrorCode,
): boolean => {
  // Any 403 invalidates session:
  // - ORG_DELETED (org became deleted after token issued)
  // - SUBORG_DELETED (suborg became deleted after token issued)
  // - ACCESS_REVOKED (generic 403, access revoked for other reasons)
  return (
    statusCode === HTTP_STATUS.FORBIDDEN &&
    (errorCode === AUTH_ERROR_CODE.ORG_DELETED ||
      errorCode === AUTH_ERROR_CODE.SUBORG_DELETED ||
      errorCode === AUTH_ERROR_CODE.ACCESS_REVOKED)
  )
}



